import React, { useState, useRef } from 'react';
import axios from 'axios';
import './ImageProcessor.css';
import HistogramDisplay from './HistogramDisplay';

interface ProcessResult {
  success: boolean;
  processed_image?: string;
  image_type?: string;
  original_size?: string;
  block_size?: string;
  error?: string;
}

const ImageProcessor: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [processedImage, setProcessedImage] = useState<string | null>(null);
  const [blockWidth, setBlockWidth] = useState<number>(3);
  const [blockHeight, setBlockHeight] = useState<number>(3);
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  const [imageInfo, setImageInfo] = useState<{
    type?: string;
    originalSize?: string;
    blockSize?: string;
  }>({});
  const [error, setError] = useState<string | null>(null);
  const [processingTime, setProcessingTime] = useState<number | null>(null);
  const [showHistograms, setShowHistograms] = useState<boolean>(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  const presetSizes = [
    { name: '2x2', width: 2, height: 2 },
    { name: '3x3', width: 3, height: 3 },
    { name: '4x4', width: 4, height: 4 },
    { name: '5x5', width: 5, height: 5 },
    { name: '8x8', width: 8, height: 8 },
    { name: '16x16', width: 16, height: 16 },
    { name: '5x12', width: 5, height: 12 },
    { name: '10x6', width: 10, height: 6 },
    { name: '25x25', width: 25, height: 25 },
    { name: '32x32', width: 32, height: 32 },
    { name: '50x50', width: 50, height: 50 },
    { name: '50x60', width: 50, height: 60 },
    { name: '80x80', width: 80, height: 80 },
    { name: '80x100', width: 80, height: 100 },
    { name: '100x100', width: 100, height: 100 },
    { name: '120x150', width: 120, height: 150 }
  ];

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          setSelectedImage(e.target?.result as string);
          setProcessedImage(null);
          setImageInfo({});
          setError(null);
        };
        reader.readAsDataURL(file);
      } else {
        setError('Please select a valid image file');
      }
    }
  };

  const processImage = async () => {
    if (!selectedImage || blockWidth <= 0 || blockHeight <= 0) {
      setError('Please select an image and enter valid block dimensions');
      return;
    }

    setIsProcessing(true);
    setError(null);
    const startTime = performance.now();

    try {
      const response = await axios.post<ProcessResult>('https://us-central1-image-processing-vk.cloudfunctions.net/process_image', {
        image: selectedImage,
        blockWidth: blockWidth,
        blockHeight: blockHeight
      });

      const endTime = performance.now();
      setProcessingTime(endTime - startTime);

      if (response.data.success) {
        setProcessedImage(response.data.processed_image!);
        setImageInfo({
          type: response.data.image_type,
          originalSize: response.data.original_size,
          blockSize: response.data.block_size
        });
      } else {
        setError(response.data.error || 'Processing failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to connect to server');
    } finally {
      setIsProcessing(false);
    }
  };

  const clearImages = () => {
    setSelectedImage(null);
    setProcessedImage(null);
    setImageInfo({});
    setError(null);
    setProcessingTime(null);
    setShowHistograms(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const applyPresetSize = (preset: { width: number; height: number }) => {
    setBlockWidth(preset.width);
    setBlockHeight(preset.height);
  };

  const downloadImage = () => {
    if (processedImage) {
      const link = document.createElement('a');
      link.href = processedImage;
      link.download = `processed_image_${blockWidth}x${blockHeight}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  return (
    <div className="image-processor">
      <div className="controls-section">
        <h3>Image Upload & Settings</h3>

        <div className="upload-section">
          <input
            type="file"
            accept="image/*"
            onChange={handleImageUpload}
            ref={fileInputRef}
            className="file-input"
          />
          <button onClick={() => fileInputRef.current?.click()} className="upload-btn">
            Choose Image
          </button>
        </div>

        <div className="block-size-section">
          <h4>Block Size Settings</h4>

          <div className="preset-buttons">
            <h5>Quick Presets:</h5>
            <div className="preset-grid">
              {presetSizes.map((preset, index) => (
                <button
                  key={index}
                  onClick={() => applyPresetSize(preset)}
                  className={`preset-btn ${blockWidth === preset.width && blockHeight === preset.height ? 'active' : ''}`}
                >
                  {preset.name}
                </button>
              ))}
            </div>
          </div>

          <div className="size-inputs">
            <label>
              Width:
              <input
                type="number"
                value={blockWidth}
                onChange={(e) => setBlockWidth(Math.max(1, parseInt(e.target.value) || 1))}
                min="1"
                className="size-input"
              />
            </label>
            <label>
              Height:
              <input
                type="number"
                value={blockHeight}
                onChange={(e) => setBlockHeight(Math.max(1, parseInt(e.target.value) || 1))}
                min="1"
                className="size-input"
              />
            </label>
          </div>
        </div>

        <div className="action-buttons">
          <button
            onClick={processImage}
            disabled={!selectedImage || isProcessing}
            className={`process-btn ${isProcessing ? 'processing' : ''}`}
          >
            {isProcessing ? 'Processing...' : 'Apply Block Averaging'}
          </button>
          <button onClick={clearImages} className="clear-btn">
            Clear All
          </button>
          <button
            onClick={() => setShowHistograms(!showHistograms)}
            className="histogram-btn"
            disabled={!selectedImage}
          >
            {showHistograms ? 'Hide' : 'Show'} Histograms
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
      </div>

      <div className="images-section">
        <div className="image-container">
          <div className="image-box">
            <h4>Original Image</h4>
            {selectedImage ? (
              <>
                <img src={selectedImage} alt="Original" className="image-display" />
                {showHistograms && <HistogramDisplay imageData={selectedImage} title="Original" />}
              </>
            ) : (
              <div className="image-placeholder">
                No image selected
              </div>
            )}
          </div>

          <div className="image-box">
            <h4>Processed Image</h4>
            {processedImage ? (
              <>
                <img src={processedImage} alt="Processed" className="image-display" />
                {showHistograms && <HistogramDisplay imageData={processedImage} title="Processed" />}
                <button onClick={downloadImage} className="download-btn">
                  Download Result
                </button>
              </>
            ) : (
              <div className="image-placeholder">
                {isProcessing ? 'Processing...' : 'No processed image'}
              </div>
            )}
          </div>
        </div>

        {(imageInfo.type || processingTime) && (
          <div className="info-section">
            <h4>Processing Information</h4>
            {imageInfo.type && <p><strong>Image Type:</strong> {imageInfo.type}</p>}
            {imageInfo.originalSize && <p><strong>Original Size:</strong> {imageInfo.originalSize}</p>}
            {imageInfo.blockSize && <p><strong>Block Size:</strong> {imageInfo.blockSize}</p>}
            {processingTime && <p><strong>Processing Time:</strong> {processingTime.toFixed(2)}ms</p>}
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageProcessor;